import { X, AlertCircle, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNotificationStore, NotificationType } from '../../store/notificationStore';

const icons: Record<NotificationType, React.ReactNode> = {
  success: <CheckCircle className="w-5 h-5 text-green-500" />,
  error: <AlertCircle className="w-5 h-5 text-red-500" />,
  warning: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
  info: <Info className="w-5 h-5 text-blue-500" />,
};

const styles: Record<NotificationType, string> = {
  success: 'bg-green-50 border-green-200',
  error: 'bg-red-50 border-red-200',
  warning: 'bg-yellow-50 border-yellow-200',
  info: 'bg-blue-50 border-blue-200',
};

const titleStyles: Record<NotificationType, string> = {
  success: 'text-green-800',
  error: 'text-red-800',
  warning: 'text-yellow-800',
  info: 'text-blue-800',
};

const messageStyles: Record<NotificationType, string> = {
  success: 'text-green-700',
  error: 'text-red-700',
  warning: 'text-yellow-700',
  info: 'text-blue-700',
};

export default function GlobalNotification() {
  const { t } = useTranslation('common');
  const { notifications, removeNotification } = useNotificationStore();

  if (notifications.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[100] space-y-2 max-w-md">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={`flex items-start gap-3 p-4 rounded-lg border shadow-lg animate-slide-in-right ${styles[notification.type]}`}
          role="alert"
        >
          <div className="flex-shrink-0 mt-0.5">
            {icons[notification.type]}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${titleStyles[notification.type]}`}>
              {notification.title}
            </p>
            <p className={`text-sm mt-1 ${messageStyles[notification.type]}`}>
              {notification.message}
            </p>
            {notification.action && (
              <button
                type="button"
                onClick={() => {
                  notification.action!.onClick();
                  removeNotification(notification.id);
                }}
                className={`text-sm font-semibold underline underline-offset-2 mt-2 hover:opacity-80 ${titleStyles[notification.type]}`}
              >
                {notification.action.label}
              </button>
            )}
          </div>
          {notification.dismissible && (
            <button
              onClick={() => removeNotification(notification.id)}
              className="flex-shrink-0 p-1 rounded-full hover:bg-black/5 transition-colors"
              aria-label={t('close')}
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
